import * as yup from "yup";

// Regular expressions for validation
const phoneNumber = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;
const URL =
  /^(((https?):)?\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

// Adding custom methods to Yup string schema
yup.addMethod(yup.string, "phone", function (message: string) {
  return this.matches(phoneNumber, { message });
});

yup.addMethod(yup.string, "url", function (message: string, options = {}) {
  const validation = this.matches(URL, { message });

  if (!options.forceWithProtocol) {
    return validation;
  }

  return validation.matches(/^(https?:\/\/)/, { message });
});

yup.addMethod(yup.string, "digit", function (message: string) {
  return this.matches(/^\d+$/, { message });
});

yup.addMethod(yup.string, "capitalize", function (message = "Please Capitalise the first letter of each word") {
  return this.test({
    name: "capitalize",
    message,
    test: function (value: any) {
      if (!value) {
        return this.createError({ message: "Required" });
      } else if (/\b\s{2,}\b/.test(value)) {
        return this.createError({ message: "Invalid input: remove extra blank spaces between words" });
      } else if (!/^\s*([A-Z]+([a-zA-Z-]{1,30})?)(\s[A-Z]+([a-zA-Z-]{1,30})?)?\s*$/.test(value)) {
        return this.createError({ message });
      }
      return true;
    }
  });
});

yup.addMethod(yup.string, "integer", function (message = "Please enter a valid integer") {
  return this.test({
    name: "integer",
    message,
    test: function (value: any) {
      if (!value) {
        return this.createError({ message: "Required" });
      } else if (isNaN(value)) {
        return this.createError({ message: "Must be a number" });
      } else if (Number(value) <= 0) {
        return this.createError({ message: "Please enter a number greater than 0" });
      }
      return true;
    }
  });
});

yup.addMethod(yup.string, "integerWithZero", function (message = "Please enter a valid integer") {
  return this.test({
    name: "integerWithZero",
    message,
    test: function (value: any) {
      if (!value) {
        return this.createError({ message: "Required" });
      } else if (isNaN(value)) {
        return this.createError({ message: "Must be a number" });
      } else if (Number(value) < 0) {
        return this.createError({ message: "Only positive numbers are allowed" });
      }
      return true;
    }
  });
});

yup.addMethod(yup.string, "percentage", function (message = "Please enter a valid percentage") {
  return this.test({
    name: "percentage",
    message,
    test: function (value: any) {
      if (!value) {
        return this.createError({ message: "Required" });
      } else if (isNaN(value)) {
        return this.createError({ message: "Must be a number" });
      } else if (Number(value) < 0) {
        return this.createError({ message: "Only positive numbers are allowed" });
      } else if (Number(value) > 100) {
        return this.createError({ message: "Value should be in range 0 - 100" });
      }
      return true;
    }
  });
});

yup.addMethod(
  yup.string,
  "year",
  function (min: number, max: number, message = `Must be a valid year between ${min} and ${max}`) {
    return this.test({
      name: "year",
      message,
      test: function (value: any) {
        if (!value) {
          return this.createError({ message: "Required" });
        } else if (isNaN(value)) {
          return this.createError({ message: "Must be a number" });
        } else {
          const year = parseInt(value, 10);
          if (year < min || year > max) {
            return this.createError({ message: `Must be between ${min} and ${max}` });
          }
        }
        return true;
      }
    });
  }
);

// Extending Yup schema with custom methods
declare module "yup" {
  interface StringSchema {
    phone(message: string): this;
    url(message: string, options?: { forceWithProtocol?: boolean }): this;
    digit(message: string): this;
    capitalize(message?: string): this;
    integer(message?: string): this;
    integerWithZero(message?: string): this;
    percentage(message?: string): this;
    year(min: number, max: number, message?: string): this;
  }
}

export default yup;
